import { useState, useCallback, useMemo, useEffect, useRef, lazy, Suspense } from 'react';
import { AnimatePresence } from 'framer-motion';
import { X, Users, PartyPopper, Star as StarIcon } from 'lucide-react';
import { MsalProvider } from '@azure/msal-react';
import { PublicClientApplication } from '@azure/msal-browser';
import { msalConfig } from './services/authConfig';
import { useAuth } from './hooks/useAuth';
import { useEmployees, ERROR_NEEDS_CONSENT } from './hooks/useEmployees';
import { useSearch } from './hooks/useSearch';
import { Layout, LoginScreen } from './components/Layout/Layout';
import { SearchBar, ViewControls } from './components/SearchBar/SearchBar';
import { EmployeeGrid, EmployeeGridSkeleton, EmployeeListSkeleton } from './components/EmployeeGrid/EmployeeGrid';
import { ProfileModal } from './components/ProfileModal/ProfileModal';
import { SelectionActionBar } from './components/SelectionActionBar/SelectionActionBar';
import { getFavorites, toggleFavorite as toggleFav } from './services/favoritesService';
import { getRecentContacts, addRecentContact } from './services/recentContactsService';
import { isAdmin, bootstrapAdmin } from './services/userRolesService';
import { Settings } from './components/Settings/Settings';
import { DepartmentList, DepartmentListSkeleton, DepartmentTeam } from './components/DepartmentList/DepartmentList';
import { FilterDrawer } from './components/FilterDrawer/FilterDrawer';
import { getNameSortKey } from './utils/nameUtils';
import { sortEmployees, isNameSort } from './utils/employeeSort';
import { parseHireDate } from './utils/hireDate';
import { loadDisplayMode, saveDisplayMode } from './utils/viewPreferences';
import { useAppConfig, loadConfig, saveConfig } from './hooks/useAppConfig';
import { useAwardMetalsConfig } from './hooks/useAwardMetalsConfig';
import { useIsDark } from './hooks/useIsDark';
import { InlineStatBadge } from './components/ui/toolbar';
import { trackAction } from './services/gamificationService';
import { getAllProfiles } from './services/profileService';
import { getEmployeeSkillSet } from './hooks/useSearch';
import { ExpertiseView } from './components/ExpertiseView/ExpertiseView';
import { CongratsInbox } from './components/CongratsInbox/CongratsInbox';
import { GrowthBanner } from './components/Growth/GrowthBanner';
import { AnniversariesView } from './components/Anniversaries/AnniversariesView';

// The org chart pulls in @xyflow/react + dagre (and its PDF export pulls in
// @react-pdf/renderer) — lazy-load the whole route so the directory's initial
// bundle stays lean.
const OrgChart = lazy(() =>
  import('./components/OrgChart/OrgChart').then((m) => ({ default: m.OrgChart }))
);

// Growth (assessments/goals/coaching) is its own big surface — lazy-load it
// the same way. The due-date banner is imported statically since it renders
// on the directory home; it only pulls the light growth services.
const GrowthView = lazy(() =>
  import('./components/Growth/GrowthView').then((m) => ({ default: m.GrowthView }))
);

const msalInstance = new PublicClientApplication(msalConfig);

// Each "Quick Selections" sidebar shortcut is independently toggleable (see
// [[app_config_settings]] in memory) rather than one all-or-nothing switch —
// a tenant might want My Direct Reports but find My Office too noisy.
const SCOPE_CONFIG_KEYS = {
  direct_reports: 'showScopeDirectReports',
  my_team: 'showScopeMyTeam',
  managers_team: 'showScopeManagersTeam',
  my_department: 'showScopeMyDepartment',
  my_office: 'showScopeMyOffice',
};

function DirectoryContent() {
  const { account, signOut, requestConsent } = useAuth();
  const { employees, photos, departments, offices, isLoading, structureReady, error, refetch, applyLocalRules } = useEmployees();
  const {
    searchQuery,
    setSearchQuery,
    selectedDepartments,
    setSelectedDepartments,
    toggleDepartment,
    selectedOffices,
    setSelectedOffices,
    toggleOffice,
    selectedSkills,
    setSelectedSkills,
    toggleSkill,
    filteredEmployees,
    clearFilters,
    savedFilters,
    deleteSavedFilter,
    applyFilter,
  } = useSearch(employees);

  const appConfig = useAppConfig();
  const isDark = useIsDark();
  const awardMetalsConfig = useAwardMetalsConfig();

  // Award-metal crest colors are shipped as CSS custom properties (index.css)
  // but are admin-editable via Settings. BadgeCelebration.jsx reads --gold-*
  // tokens directly (independent of AwardBadge.jsx), so overrides must be
  // applied globally as real custom properties on the root element, not
  // threaded through component props. Inline styles win over both the
  // `:root` and `html.dark` stylesheet rules regardless of source order, so
  // this correctly overrides whichever theme is currently showing. Every
  // field is set explicitly every run (even ones equal to the shipped
  // default) so that resetting a single field never leaves a stale inline
  // override from a previous save.
  useEffect(() => {
    const themeColors = awardMetalsConfig[isDark ? 'dark' : 'light'];
    for (const metal of Object.keys(themeColors)) {
      const fields = themeColors[metal];
      for (const field of Object.keys(fields)) {
        document.documentElement.style.setProperty(`--${metal}-${field}`, fields[field]);
      }
    }
  }, [awardMetalsConfig, isDark]);

  const [currentView, setCurrentView] = useState(() => loadConfig().defaultView || 'grid');
  // 'directory' | 'favorites' | 'anniversaries' — tabs within the main grid
  // view (Directory's own destination), not separate sidebar destinations.
  const [directoryTab, setDirectoryTab] = useState('directory');
  const [displayMode, setDisplayMode] = useState(() => loadDisplayMode() || loadConfig().defaultDisplayMode || 'list');
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [modalOrigin, setModalOrigin] = useState({ x: 0, y: 0 });
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [recentContacts, setRecentContacts] = useState([]);
  const [selectedSmartGroup, setSelectedSmartGroup] = useState(null);

  const currentUserId = account?.localAccountId;

  // Run synchronously so isAdmin() sees the correct role on the very first render.
  // bootstrapAdmin is idempotent so repeated calls from StrictMode double-renders are harmless.
  if (currentUserId) bootstrapAdmin(currentUserId);

  useEffect(() => {
    if (currentUserId) {
      setFavorites(getFavorites(currentUserId));
      setRecentContacts(getRecentContacts(currentUserId));
    }
  }, [currentUserId]);

  const toggleFavorite = useCallback((employeeId) => {
    setFavorites(toggleFav(currentUserId, employeeId));
  }, [currentUserId]);

  const toggleEmployeeSelection = useCallback((employee) => {
    setSelectedEmployees((prev) => {
      const isSelected = prev.some((e) => e.id === employee.id);
      return isSelected ? prev.filter((e) => e.id !== employee.id) : [...prev, employee];
    });
  }, []);

  const clearSelection = useCallback(() => setSelectedEmployees([]), []);

  const employeeCountByDept = useMemo(() => {
    const counts = {};
    employees.forEach((emp) => {
      const dept = emp.department || 'Unassigned';
      counts[dept] = (counts[dept] || 0) + 1;
    });
    return counts;
  }, [employees]);

  const employeeCountByOffice = useMemo(() => {
    const counts = {};
    employees.forEach((emp) => {
      const office = emp.officeLocation || 'No Office';
      counts[office] = (counts[office] || 0) + 1;
    });
    return counts;
  }, [employees]);

  const currentUser = useMemo(
    () => employees.find((emp) => emp.id === currentUserId),
    [employees, currentUserId]
  );

  // Mockup 6B: all five scopes, always, with a reason when one is empty.
  //
  // These used to be pushed only when their count was above zero, so a scope that
  // did not apply to you vanished — which is why nobody knows "My Direct Reports"
  // exists: for the ~700 people who have none it has never once rendered. It also
  // meant the sidebar section changed length as currentUser resolved out of 739
  // records, and Layout's disabled-with-reason branch was unreachable code.
  const smartGroups = useMemo(() => {
    if (!currentUser || !employees.length) return [];
    const { id: userId, managerId, department, officeLocation } = currentUser;

    const directReports = employees.filter((e) => e.managerId === userId);
    const myTeam = managerId
      ? employees.filter((e) => e.managerId === managerId && e.id !== userId)
      : [];
    const managersTeam = managerId
      ? employees.filter((e) => e.managerId === managerId || e.id === managerId)
      : [];
    const myDept = department
      ? employees.filter((e) => e.department === department && e.id !== userId)
      : [];
    const myOffice = officeLocation
      ? employees.filter((e) => e.officeLocation === officeLocation && e.id !== userId)
      : [];

    return [
      {
        id: 'direct_reports',
        name: 'My Direct Reports',
        count: directReports.length,
        reason: 'Nobody reports to you in the directory',
      },
      {
        id: 'my_team',
        name: 'My Team',
        count: myTeam.length,
        reason: managerId
          ? 'You are the only person reporting to your manager'
          : 'No manager on your record, so there is no team to scope to',
      },
      {
        id: 'managers_team',
        name: "My Manager's Team",
        // The manager plus their reports; one means the manager alone, which is
        // the same view as My Team and not worth offering.
        count: managersTeam.length > 1 ? managersTeam.length : 0,
        reason: managerId
          ? 'Your manager has no other reports'
          : 'No manager on your record',
      },
      {
        id: 'my_department',
        name: 'My Department',
        count: myDept.length,
        reason: department
          ? `You are the only person in ${department}`
          : 'No department on your record',
      },
      {
        id: 'my_office',
        name: 'My Office',
        count: myOffice.length,
        reason: officeLocation
          ? `You are the only person in ${officeLocation}`
          : 'No office on your record',
      },
    ].filter((group) => appConfig[SCOPE_CONFIG_KEYS[group.id]]);
  }, [currentUser, employees, appConfig]);

  const anyScopeEnabled = Object.values(SCOPE_CONFIG_KEYS).some((key) => appConfig[key]);

  // The scope list depends on currentUser resolving out of 739 records, so it
  // arrives late. Say so, rather than letting the section pop in.
  const scopesLoading = anyScopeEnabled && (isLoading || (!currentUser && Boolean(currentUserId)));

  // Mockup 6A: an active scope produced no feedback in the page at all — the list
  // simply got shorter with no statement of why, and the only way to clear it was
  // three levels into the filter drawer.
  const activeScope = useMemo(() => {
    if (!selectedSmartGroup) return null;
    const group = smartGroups.find((g) => g.id === selectedSmartGroup);
    if (!group) return null;
    const manager = currentUser?.managerId
      ? employees.find((e) => e.id === currentUser.managerId)
      : null;
    const resolved = {
      my_department: currentUser?.department,
      my_office: currentUser?.officeLocation,
      managers_team: manager?.displayName,
      my_team: manager?.displayName ? `reports to ${manager.displayName}` : null,
      direct_reports: null,
    }[selectedSmartGroup];
    return { ...group, resolved };
  }, [selectedSmartGroup, smartGroups, currentUser, employees]);

  const smartGroupFilteredEmployees = useMemo(() => {
    if (!selectedSmartGroup || !currentUser) return filteredEmployees;
    const { id: userId, managerId, department, officeLocation } = currentUser;

    switch (selectedSmartGroup) {
      case 'direct_reports':
        return filteredEmployees.filter((e) => e.managerId === userId);
      case 'my_team':
        return filteredEmployees.filter((e) => e.managerId === managerId && e.id !== userId);
      case 'managers_team':
        return filteredEmployees.filter((e) => e.managerId === managerId || e.id === managerId);
      case 'my_department':
        return filteredEmployees.filter((e) => e.department === department && e.id !== userId);
      case 'my_office':
        return filteredEmployees.filter((e) => e.officeLocation === officeLocation && e.id !== userId);
      default:
        return filteredEmployees;
    }
  }, [filteredEmployees, selectedSmartGroup, currentUser]);

  const selectAll = useCallback(
    () => setSelectedEmployees(smartGroupFilteredEmployees),
    [smartGroupFilteredEmployees]
  );

  // Which tab the profile modal opens on — 'overview' normally; 'badges'
  // when entered via a badge cluster on a card/row.
  const [profileInitialSection, setProfileInitialSection] = useState('overview');

  // The pane sticks below the sticky page header. Measured rather than
  // hard-coded, so changing the header's height cannot misalign it.
  const headerRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(128);
  useEffect(() => {
    const el = headerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => setHeaderHeight(el.offsetHeight));
    ro.observe(el);
    setHeaderHeight(el.offsetHeight);
    return () => ro.disconnect();
  }, []);

  const handleEmployeeClick = useCallback((employee, rect, section = 'overview') => {
    setProfileInitialSection(section);
    const fullEmployee = employees.find((e) => e.id === employee.id) || employee;
    if (rect) {
      setModalOrigin({
        x: (rect.left + rect.width / 2) - window.innerWidth / 2,
        y: (rect.top + rect.height / 2) - window.innerHeight / 2,
      });
    } else {
      setModalOrigin({ x: 0, y: 0 });
    }
    setSelectedEmployee(fullEmployee);
    if (currentUserId && fullEmployee.id) {
      addRecentContact(currentUserId, fullEmployee.id, fullEmployee.displayName);
      setRecentContacts(getRecentContacts(currentUserId));
    }
  }, [employees, currentUserId]);

  // "View in Org Chart" from a profile's Team tab: close the modal, switch
  // views, and hand the org chart a focus target to center once it settles.
  const [orgChartFocusId, setOrgChartFocusId] = useState(null);
  const handleViewInOrgChart = useCallback((employee) => {
    setSelectedEmployee(null);
    setOrgChartFocusId(employee.id);
    setCurrentView('orgchart');
  }, []);

  const handleSelectFromModal = useCallback((employee) => {
    const fullEmployee = employees.find((e) => e.id === employee.id) || employee;
    setSelectedEmployee(fullEmployee);
    if (currentUserId && fullEmployee.id) {
      addRecentContact(currentUserId, fullEmployee.id, fullEmployee.displayName);
      setRecentContacts(getRecentContacts(currentUserId));
    }
  }, [employees, currentUserId]);

  const user = account
    ? { name: account.name, username: account.username, photo: currentUser ? photos[currentUserId] : null }
    : null;

  // Derive displayed employee list based on current view
  const displayedEmployees = useMemo(() => {
    if (currentView === 'grid' && directoryTab === 'favorites')
      return smartGroupFilteredEmployees.filter((e) => favorites.includes(e.id));
    if (currentView === 'recent')
      return smartGroupFilteredEmployees.filter((e) =>
        recentContacts.some((rc) => rc.employeeId === e.id)
      );
    return smartGroupFilteredEmployees;
  }, [currentView, directoryTab, smartGroupFilteredEmployees, favorites, recentContacts]);

  const [sortBy, setSortBy] = useState(() => loadConfig().defaultSortBy || 'lastName');
  const [selectedLetter, setSelectedLetter] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [settingsIntent, setSettingsIntent] = useState(null);

  useEffect(() => {
    if (currentView === 'orgchart' && !appConfig.showOrgChart) setCurrentView('grid');
    if (currentView === 'recent' && !appConfig.showRecentContacts) setCurrentView('grid');
    if (currentView === 'department' && !appConfig.showDepartments) setCurrentView('grid');
    if (currentView === 'expertise' && !appConfig.showExpertise) setCurrentView('grid');
    if (currentView === 'growth' && !appConfig.showGrowth) setCurrentView('grid');
    if (selectedSmartGroup && !smartGroups.some((g) => g.id === selectedSmartGroup)) setSelectedSmartGroup(null);
  }, [appConfig, currentView, selectedSmartGroup, smartGroups]);

  // The Favorites tab only exists while it's admin-enabled AND the user has
  // at least one favorite — fall back to the Directory tab the moment either
  // stops being true (toggled off, or the last favorite gets removed while
  // that tab is open).
  useEffect(() => {
    if (directoryTab === 'favorites' && (!appConfig.showFavorites || favorites.length === 0)) {
      setDirectoryTab('directory');
    }
  }, [directoryTab, appConfig.showFavorites, favorites.length]);

  useEffect(() => {
    if (currentView === 'settings' && !isAdmin(currentUserId)) {
      setCurrentView('grid');
    }
  }, [currentView, currentUserId]);

  const clearAllFilters = useCallback(() => {
    clearFilters();
    setSelectedSmartGroup(null);
    setSelectedLetter(null);
    setSelectedDepartment(null);
  }, [clearFilters]);

  // Reset selected department when navigating away from the department view
  useEffect(() => {
    if (currentView !== 'department') setSelectedDepartment(null);
  }, [currentView]);

  useEffect(() => {
    if (currentView !== 'settings' && settingsIntent) setSettingsIntent(null);
  }, [currentView, settingsIntent]);

  // Mockup 6B: "No employees found · Clear All Filters" named nothing and offered
  // one blunt undo, so the usual cause — a scope you forgot was on, plus a search —
  // was invisible. Every active constraint is now listed with its own removal, so
  // the fix can be the narrowest one.
  const activeConstraints = useMemo(() => {
    const list = [];
    if (searchQuery.trim()) {
      list.push({ key: 'search', label: `Search “${searchQuery.trim()}”`, clear: () => setSearchQuery('') });
    }
    if (selectedSmartGroup) {
      const scope = smartGroups.find((g) => g.id === selectedSmartGroup);
      list.push({
        key: 'scope',
        label: `Scope ${scope?.name ?? selectedSmartGroup}`,
        clear: () => setSelectedSmartGroup(null),
      });
    }
    if (selectedDepartments.length) {
      list.push({
        key: 'departments',
        label: selectedDepartments.length === 1
          ? `Department ${selectedDepartments[0]}`
          : `${selectedDepartments.length} departments`,
        clear: () => setSelectedDepartments([]),
      });
    }
    if (selectedOffices.length) {
      list.push({
        key: 'offices',
        label: selectedOffices.length === 1 ? `Office ${selectedOffices[0]}` : `${selectedOffices.length} offices`,
        clear: () => setSelectedOffices([]),
      });
    }
    if (selectedSkills.length) {
      list.push({
        key: 'skills',
        label: selectedSkills.length === 1 ? `Skill ${selectedSkills[0]}` : `${selectedSkills.length} skills`,
        clear: () => setSelectedSkills([]),
      });
    }
    if (selectedLetter) {
      list.push({ key: 'letter', label: `Surname ${selectedLetter}`, clear: () => setSelectedLetter(null) });
    }
    return list;
  }, [
    searchQuery, setSearchQuery, selectedSmartGroup, smartGroups,
    selectedDepartments, setSelectedDepartments,
    selectedOffices, setSelectedOffices,
    selectedSkills, setSelectedSkills, selectedLetter,
  ]);

  // A text search ranks results by relevance (useSearch's Fuse ordering); the
  // normal directory sort would discard that ranking, so only apply it when
  // there's no active query. Facet-only filtering (dept/office/skills with no
  // text) still gets the usual sort.
  const sortedEmployees = useMemo(
    () => (searchQuery.trim() ? displayedEmployees : sortEmployees(displayedEmployees, sortBy)),
    [displayedEmployees, sortBy, searchQuery],
  );

  // The A–Z rail is only meaningful under a name sort; a department or tenure
  // sort produces letters in arbitrary order.
  const availableLetters = useMemo(() => {
    if (!isNameSort(sortBy)) return [];
    const letters = new Set(sortedEmployees.map((e) =>
      getNameSortKey(e.displayName, sortBy).charAt(0).toUpperCase()
    ).filter((l) => l && l !== '#'));
    return [...letters].sort();
  }, [sortedEmployees, sortBy]);

  const letterFilteredEmployees = useMemo(() => {
    if (!selectedLetter || !isNameSort(sortBy)) return sortedEmployees;
    return sortedEmployees.filter((e) =>
      getNameSortKey(e.displayName, sortBy).charAt(0).toUpperCase() === selectedLetter
    );
  }, [sortedEmployees, selectedLetter, sortBy]);

  // Switching to a non-name sort must clear any active letter filter, or the
  // list silently keeps filtering by a letter the user can no longer see.
  useEffect(() => {
    if (!isNameSort(sortBy) && selectedLetter) setSelectedLetter(null);
  }, [sortBy, selectedLetter]);

  useEffect(() => {
    const config = loadConfig();
    if (config.defaultSortBy === sortBy) return;
    saveConfig({ ...config, defaultSortBy: sortBy });
  }, [sortBy]);

  // Personal, per-device cache — NOT the tenant-wide admin default. saveConfig()
  // pushes to the shared app-config blob (admin-gated), which would be wrong
  // here: one user's own list/card pick must never overwrite what every other
  // user sees. See utils/viewPreferences.js.
  useEffect(() => {
    saveDisplayMode(displayMode);
  }, [displayMode]);

  // Skill tags across all sources, with people counts — feeds the FilterDrawer
  // facet and the Expertise view.
  // Carries the holders and their offices, not just a count: the Expertise view
  // needs distinct tagged people (someone with four skills is one person), a face
  // stack per skill, and how many offices a capability spans.
  const allSkills = useMemo(() => {
    const bySkill = new Map();
    const profiles = getAllProfiles();
    employees.forEach((e) => {
      getEmployeeSkillSet(e, profiles[e.id] || {}).forEach((skill) => {
        let entry = bySkill.get(skill);
        if (!entry) {
          entry = { skill, people: [], offices: new Set(), byOffice: new Map() };
          bySkill.set(skill, entry);
        }
        entry.people.push({ id: e.id, displayName: e.displayName });
        if (e.officeLocation) {
          entry.offices.add(e.officeLocation);
          // Per-office holders, for the coverage map: "can Reno run this pursuit
          // alone" needs the breakdown, not just how many offices are involved.
          entry.byOffice.set(e.officeLocation, (entry.byOffice.get(e.officeLocation) ?? 0) + 1);
        }
      });
    });
    return [...bySkill.values()]
      .map((entry) => ({
        skill: entry.skill,
        count: entry.people.length,
        people: entry.people,
        officeCount: entry.offices.size,
        byOffice: entry.byOffice,
      }))
      .sort((a, b) => b.count - a.count || a.skill.localeCompare(b.skill));
  }, [employees]);

  // Work anniversaries: hired this calendar month in a prior year.
  const anniversariesThisMonth = useMemo(() => {
    const now = new Date();
    return employees
      .filter((e) => {
        const hired = parseHireDate(e.hireDate);
        return hired
          && hired.getMonth() === now.getMonth()
          && hired.getFullYear() < now.getFullYear();
      })
      .map((e) => ({ employee: e, years: now.getFullYear() - parseHireDate(e.hireDate).getFullYear() }))
      .sort((a, b) => b.years - a.years);
  }, [employees]);

  const isViewingAnniversaries = currentView === 'grid' && appConfig.showAnniversaries && directoryTab === 'anniversaries';
  const showFavoritesTab = appConfig.showFavorites && favorites.length > 0;

  // Gamification triggers: searching and visiting the org chart count toward
  // their badges. trackAction is cheap and idempotent per badge.
  useEffect(() => {
    if (currentUserId && searchQuery.trim()) trackAction(currentUserId, 'search');
  }, [currentUserId, searchQuery]);
  useEffect(() => {
    if (currentUserId && currentView === 'orgchart') trackAction(currentUserId, 'view_org_chart');
  }, [currentUserId, currentView]);

  return (
    <>
    <Layout
      currentView={currentView}
      onViewChange={setCurrentView}
      user={user}
      onSignOut={signOut}
      onOpenMyProfile={() => { if (currentUser) handleEmployeeClick(currentUser); }}
      recentContactsCount={recentContacts.length}
      departments={departments}
      selectedDepartments={selectedDepartments}
      onToggleDepartment={toggleDepartment}
      employeeCountByDept={employeeCountByDept}
      offices={offices}
      selectedOffices={selectedOffices}
      onToggleOffice={toggleOffice}
      employeeCountByOffice={employeeCountByOffice}
      smartGroups={smartGroups}
      scopesLoading={scopesLoading}
      selectedSmartGroup={selectedSmartGroup}
      onSelectSmartGroup={setSelectedSmartGroup}
      savedFilters={savedFilters}
      onApplySavedFilter={applyFilter}
      isAdmin={isAdmin(currentUserId)}
      showDepartments={appConfig.showDepartments}
      showExpertise={appConfig.showExpertise}
      showOrgChart={appConfig.showOrgChart}
      showRecentContacts={appConfig.showRecentContacts}
      showGrowth={appConfig.showGrowth}
      showSmartGroups={anyScopeEnabled}
      searchQuery={searchQuery}
      onSearchChange={setSearchQuery}
    >
      {currentView === 'settings' && isAdmin(currentUserId) && (
        <Settings
          employees={employees}
          photos={photos}
          currentUserId={currentUserId}
          allSkills={allSkills}
          onDirectoryRefresh={refetch}
          initialTab={settingsIntent?.tab ?? null}
          peopleAccessReviewFilter={settingsIntent?.peopleAccessReviewFilter ?? null}
        />
      )}

      {currentView !== 'settings' && error === ERROR_NEEDS_CONSENT && (
        <div className="mb-6 p-5 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/50 rounded-xl">
          <p className="font-semibold text-amber-800 dark:text-amber-300 mb-1">Additional permissions required</p>
          <p className="text-sm text-amber-700 dark:text-amber-400 mb-4">
            PeopleVUE needs <strong>User.Read.All</strong> and <strong>Presence.Read.All</strong> to load the directory.
            These require admin consent in the Azure portal, then a fresh sign-in.
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={async () => { try { await requestConsent(); window.location.reload(); } catch { /* user closed the consent popup */ } }}
              className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium rounded-lg transition-colors"
            >
              Grant permissions
            </button>
            <button
              onClick={signOut}
              className="px-4 py-2 bg-white dark:bg-zinc-800 border border-amber-300 dark:border-zinc-600 text-amber-700 dark:text-amber-400 text-sm font-medium rounded-lg transition-colors hover:bg-amber-50 dark:hover:bg-zinc-700"
            >
              Sign out &amp; sign in again
            </button>
          </div>
        </div>
      )}

      {currentView !== 'settings' && error && error !== ERROR_NEEDS_CONSENT && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700/50 rounded-xl text-red-700 dark:text-red-400">
          <p className="font-medium">Failed to load employees</p>
          <p className="text-sm mt-1">{error}</p>
        </div>
      )}

      {/* Org Chart / Grid views — hidden when in settings */}
      {currentView !== 'settings' && currentView === 'orgchart' ? (
        <div className="h-full overflow-hidden">
          {isLoading || !structureReady ? (
            <div className="flex flex-col items-center justify-center h-full gap-3">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-500 border-t-transparent" />
              <p className="text-sm text-slate-400 dark:text-zinc-500">Building reporting lines…</p>
            </div>
          ) : (
            <Suspense
              fallback={
                <div className="flex flex-col items-center justify-center h-full gap-3">
                  <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-500 border-t-transparent" />
                  <p className="text-sm text-slate-400 dark:text-zinc-500">Loading org chart…</p>
                </div>
              }
            >
              <OrgChart
                employees={employees}
                photos={photos}
                onEmployeeClick={handleEmployeeClick}
                isAdmin={isAdmin(currentUserId)}
                onDataChange={refetch}
                focusEmployeeId={orgChartFocusId}
                onFocusHandled={() => setOrgChartFocusId(null)}
                currentUserId={currentUserId}
              />
            </Suspense>
          )}
        </div>
      ) : currentView === 'expertise' ? (
        <ExpertiseView
          employees={employees}
          photos={photos}
          allSkills={allSkills}
          isDark={isDark}
          onSelectEmployee={handleEmployeeClick}
          onBrowsePeople={(skill) => {
            // Clears first so the directory shows everyone with this skill rather
            // than the intersection with whatever was already filtered — an
            // intersection can be empty, which reads as "nobody has this skill".
            // The filter chip in the header states what is applied on arrival.
            clearAllFilters();
            toggleSkill(skill);
            setCurrentView('grid');
          }}
        />
      ) : currentView === 'growth' ? (
        <Suspense
          fallback={
            <div className="flex flex-col items-center justify-center h-full gap-3">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-500 border-t-transparent" />
              <p className="text-sm text-slate-400 dark:text-zinc-500">Loading Growth…</p>
            </div>
          }
        >
          <GrowthView
            employees={employees}
            photos={photos}
            currentUserId={currentUserId}
            isAdmin={isAdmin(currentUserId)}
          />
        </Suspense>
      ) : currentView !== 'settings' ? (
        /* All grid-based views */
        <div className="w-full">
          {/* Search Bar — sticky on mobile */}
          {/* Sticky page header. Background is the canvas token so it reads as
              the page continuing rather than a translucent slab over it. */}
          <div
            ref={headerRef}
            className="sticky top-0 z-10 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 pt-1 pb-3 mb-3
                       bg-canvas"
          >
            <SearchBar
              totalCount={employees.length}
              filteredCount={letterFilteredEmployees.length}
              savedFilters={isViewingAnniversaries ? [] : savedFilters}
              onDeleteFilter={deleteSavedFilter}
              onApplyFilter={applyFilter}
            />
          </div>

          {/* .scopeband — names the scope AND its resolved value, states the count,
              and carries the clear affordance on the pill itself. */}
          {activeScope && (
            <div className="flex items-center gap-2.5 flex-wrap mb-3">
              <span className="inline-flex items-center gap-2 h-[30px] pl-3 pr-1.5 rounded-full
                               bg-accent border border-accent text-[var(--on-accent)] text-[12px] font-bold whitespace-nowrap">
                <span className="text-[9.5px] font-bold uppercase tracking-[0.1em] opacity-[0.78]">Scope</span>
                {activeScope.name}
                {activeScope.resolved && <span className="font-semibold opacity-90">· {activeScope.resolved}</span>}
                <button
                  onClick={() => setSelectedSmartGroup(null)}
                  title="Clear scope"
                  aria-label="Clear scope"
                  className="w-5 h-5 rounded-full bg-white/20 inline-flex items-center justify-center"
                >
                  <X size={11} />
                </button>
              </span>
              <span className="text-[12px] text-ink-3">
                Showing <b className="font-mono font-semibold text-ink-2">{letterFilteredEmployees.length}</b>
                {' of '}{employees.length}
              </span>
            </div>
          )}

          {/* Congrats you've received — directory home only */}
          {currentView === 'grid' && !searchQuery.trim() && (
            <>
              {appConfig.showGrowth && (
                <GrowthBanner currentUserId={currentUserId} onOpen={() => setCurrentView('growth')} />
              )}
              <CongratsInbox currentUserId={currentUserId} photos={photos} />
            </>
          )}

          {/* Employee Grid */}
          {isLoading ? (
            displayMode === 'list'
              ? <EmployeeListSkeleton count={15} />
              : <EmployeeGridSkeleton count={12} />
          ) : currentView === 'recent' && recentContacts.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-20 h-20 mx-auto mb-4 bg-slate-100 dark:bg-zinc-800 rounded-full flex items-center justify-center">
                <svg className="w-10 h-10 text-slate-400 dark:text-zinc-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-slate-800 dark:text-zinc-100 mb-2">No recent contacts</h3>
              <p className="text-slate-500 dark:text-zinc-400 mb-6 max-w-md mx-auto">
                People you view will appear here for quick access.
              </p>
              <button
                onClick={() => setCurrentView('grid')}
                className="px-5 py-2.5 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors font-medium"
              >
                Browse Directory
              </button>
            </div>
          ) : displayedEmployees.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-20 h-20 mx-auto mb-4 bg-slate-100 dark:bg-zinc-800 rounded-full flex items-center justify-center">
                <svg className="w-10 h-10 text-slate-400 dark:text-zinc-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <h3 className="text-[17px] font-bold text-ink mb-1.5">
                {activeConstraints.length > 1
                  ? `Nobody matches all ${activeConstraints.length} of these`
                  : 'Nobody matches this'}
              </h3>
              <p className="text-[12.5px] text-ink-3 mb-4 max-w-[46ch] mx-auto leading-[1.55]">
                {activeConstraints.length
                  ? 'Remove the one you care about least — each chip drops just that constraint.'
                  : 'The directory is empty for this view.'}
              </p>
              {activeConstraints.length > 0 && (
                <div className="flex items-center justify-center gap-2 flex-wrap mb-4 max-w-[36rem] mx-auto">
                  {activeConstraints.map((constraint) => (
                    <button
                      key={constraint.key}
                      onClick={constraint.clear}
                      className="inline-flex items-center gap-1.5 h-7 pl-3 pr-2 rounded-full border border-line
                                 bg-surface text-[12px] font-semibold text-ink-2 hover:border-accent hover:text-accent transition-colors"
                    >
                      {constraint.label}
                      <X size={12} />
                    </button>
                  ))}
                </div>
              )}
              {activeConstraints.length > 1 && (
                <button
                  onClick={clearAllFilters}
                  className="text-[12px] font-semibold text-ink-3 hover:text-accent underline decoration-dotted underline-offset-2"
                >
                  Clear everything
                </button>
              )}
            </div>
          ) : currentView === 'department' && !selectedDepartment ? (
            /* Department landing — all departments as cards. Waits for
               structureReady so corrections/renames are in the FIRST paint
               instead of flashing old department names. */
            !structureReady ? (
              <DepartmentListSkeleton />
            ) : (
            <>
              <div className="flex items-center gap-1 mb-3 px-1">
                <InlineStatBadge label="departments" value={departments.length} isDark={isDark} />
                <InlineStatBadge label="employees" value={employees.length} isDark={isDark} />
              </div>
              <DepartmentList
                departments={departments}
                employees={employees}
                photos={photos}
                onSelectDepartment={setSelectedDepartment}
                isAdmin={isAdmin(currentUserId)}
                onSettingsChange={applyLocalRules}
                onReviewUnassigned={isAdmin(currentUserId) ? () => {
                  setSettingsIntent({
                    tab: 'users',
                    peopleAccessReviewFilter: 'missingDepartment',
                  });
                  setCurrentView('settings');
                } : undefined}
              />
            </>
            )
          ) : currentView === 'department' && selectedDepartment ? (
            /* Department team page — hero, lead spotlight, members by office */
            <DepartmentTeam
              department={selectedDepartment}
              employees={sortedEmployees.filter((e) => e.department === selectedDepartment)}
              departmentCounts={employeeCountByDept}
              photos={photos}
              onSelectEmployee={handleEmployeeClick}
              onBack={() => setSelectedDepartment(null)}
              isAdmin={isAdmin(currentUserId)}
              onSettingsChange={applyLocalRules}
              selectedEmployees={selectedEmployees}
              onToggleSelection={toggleEmployeeSelection}
              onSelectMany={setSelectedEmployees}
              stickyTop={headerHeight}
            />
          ) : (
            /* pr-10 at lg+ reserves the gutter the A–Z rail occupies, so
               the rail aligns to the table top, stays close to the rows, and
               never sits over the favourite column or the scrollbar. */
            /* Capped and centred: at 1900px an uncapped table left ~1000px of
               empty canvas to the right of the last column. */
            <div className="lg:pr-7 max-w-[1580px] mx-auto">
              {/* Tab row + view controls stay put — the filter drawer below
                  only pushes the directory content, never this row. */}
              <div className="flex items-center justify-between gap-2 mb-4">
                {currentView === 'grid' && (appConfig.showAnniversaries || showFavoritesTab) ? (
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setDirectoryTab('directory')}
                      className={`inline-flex items-center gap-2 pl-3.5 pr-4 h-9 rounded-full text-[13px] font-semibold transition-colors
                        ${directoryTab === 'directory'
                          ? 'bg-accent text-[var(--on-accent)] shadow-e1'
                          : 'bg-surface-2 border border-line-strong text-ink-2 hover:border-accent-line hover:text-ink'}`}
                    >
                      <Users size={15} />
                      Directory
                    </button>
                    {appConfig.showAnniversaries && (
                      <button
                        type="button"
                        onClick={() => setDirectoryTab('anniversaries')}
                        className={`inline-flex items-center gap-2 pl-3.5 pr-4 h-9 rounded-full text-[13px] font-semibold transition-colors
                          ${directoryTab === 'anniversaries'
                            ? 'bg-accent text-[var(--on-accent)] shadow-e1'
                            : 'bg-surface-2 border border-line-strong text-ink-2 hover:border-accent-line hover:text-ink'}`}
                      >
                        <PartyPopper size={15} />
                        Anniversaries
                        {anniversariesThisMonth.length > 0 && (
                          <span className={`font-mono text-[10.5px] ${directoryTab === 'anniversaries' ? 'text-[var(--on-accent)] opacity-80' : 'text-ink-4'}`}>
                            {anniversariesThisMonth.length}
                          </span>
                        )}
                      </button>
                    )}
                    {showFavoritesTab && (
                      <button
                        type="button"
                        onClick={() => setDirectoryTab('favorites')}
                        className={`inline-flex items-center gap-2 pl-3.5 pr-4 h-9 rounded-full text-[13px] font-semibold transition-colors
                          ${directoryTab === 'favorites'
                            ? 'bg-accent text-[var(--on-accent)] shadow-e1'
                            : 'bg-surface-2 border border-line-strong text-ink-2 hover:border-accent-line hover:text-ink'}`}
                      >
                        <StarIcon size={15} />
                        Favorites
                        <span className={`font-mono text-[10.5px] ${directoryTab === 'favorites' ? 'text-[var(--on-accent)] opacity-80' : 'text-ink-4'}`}>
                          {favorites.length}
                        </span>
                      </button>
                    )}
                  </div>
                ) : <div />}

                <ViewControls
                  displayMode={displayMode}
                  onDisplayModeChange={setDisplayMode}
                  selectedEmployees={selectedEmployees}
                  onSelectAll={isViewingAnniversaries ? undefined : selectAll}
                  onDeselectAll={isViewingAnniversaries ? undefined : clearSelection}
                  totalCount={employees.length}
                  onOpenFilterDrawer={isViewingAnniversaries ? undefined : () => setFilterDrawerOpen((v) => !v)}
                  isFilterDrawerOpen={filterDrawerOpen}
                  activeFilterCount={selectedDepartments.length + selectedOffices.length + selectedSkills.length + (selectedSmartGroup ? 1 : 0)}
                  onClearAll={isViewingAnniversaries ? undefined : clearAllFilters}
                />
              </div>

              <div className="flex items-start gap-4">
              <div className="min-w-0 flex-1">
              {isViewingAnniversaries ? (
                <AnniversariesView
                  anniversaries={anniversariesThisMonth}
                  photos={photos}
                  onSelectEmployee={handleEmployeeClick}
                  displayMode={displayMode}
                />
              ) : (
                <EmployeeGrid
                  employees={letterFilteredEmployees}
                  photos={photos}
                  onEmployeeClick={handleEmployeeClick}
                  groupByDepartment={false}
                  groupByOffice={currentView === 'office'}
                  sortBy={sortBy}
                  onSortChange={setSortBy}
                  selectedEmployees={selectedEmployees}
                  onToggleSelection={toggleEmployeeSelection}
                  favorites={favorites}
                  onToggleFavorite={toggleFavorite}
                  displayMode={displayMode}
                  recentContacts={recentContacts}
                  allEmployees={employees}
                  currentUserId={currentUserId}
                  onBadgesClick={(employee, rect) => handleEmployeeClick(employee, rect, 'badges')}
                  availableLetters={currentView !== 'office' ? availableLetters : []}
                  selectedLetter={selectedLetter}
                  onLetterClick={(letter) => setSelectedLetter((prev) => prev === letter ? null : letter)}
                />
              )}
              </div>

              <FilterDrawer
              isOpen={filterDrawerOpen}
              onClose={() => setFilterDrawerOpen(false)}
              sortBy={sortBy}
              onSortByChange={setSortBy}
              departments={departments}
              selectedDepartments={selectedDepartments}
              onToggleDepartment={toggleDepartment}
              employeeCountByDept={employeeCountByDept}
              offices={offices}
              selectedOffices={selectedOffices}
              onToggleOffice={toggleOffice}
              employeeCountByOffice={employeeCountByOffice}
              smartGroups={smartGroups}
              selectedSmartGroup={selectedSmartGroup}
              onSelectSmartGroup={setSelectedSmartGroup}
              savedFilters={savedFilters}
              onApplySavedFilter={applyFilter}
              onClearAll={clearAllFilters}
            />
              </div>
            </div>
          )}
        </div>
      ) : null}

      <AnimatePresence>
        {selectedEmployee && (
          <ProfileModal
            key={selectedEmployee.id}
            employee={selectedEmployee}
            photo={photos[selectedEmployee.id]}
            photos={photos}
            allEmployees={employees}
            departments={departments}
            onClose={() => setSelectedEmployee(null)}
            onSelectEmployee={handleSelectFromModal}
            currentUserId={currentUserId}
            onProfileUpdate={refetch}
            isFavorite={favorites.includes(selectedEmployee.id)}
            onToggleFavorite={toggleFavorite}
            isAdmin={isAdmin(currentUserId)}
            onExclude={refetch}
            onViewInOrgChart={appConfig.showOrgChart ? handleViewInOrgChart : undefined}
            initialSection={profileInitialSection}
            originX={modalOrigin.x}
            originY={modalOrigin.y}
          />
        )}
      </AnimatePresence>

      <SelectionActionBar
        selectedEmployees={selectedEmployees}
        onClearSelection={clearSelection}
        currentUserId={currentUserId}
        photos={photos}
        displayMode={displayMode}
      />

    </Layout>
    </>
  );
}

function AppContent() {
  const { isAuthenticated, isLoading, error, signIn } = useAuth();
  if (!isAuthenticated) {
    return <LoginScreen onSignIn={signIn} isLoading={isLoading} error={error} />;
  }
  return <DirectoryContent />;
}

export default function App() {
  return (
    <MsalProvider instance={msalInstance}>
      <AppContent />
    </MsalProvider>
  );
}
